// This module is intended for text that specifically cannot be handled by
// the labels.js module.
// Anything requiring translation will typically be targetting a template literal.
// Anything not requiring translation will typically just be a function that lives
// here and doesn't use the formatter or translater modules.
// Formatter args:
// 1: Required.  Key(s) you wanna translate.  Pass single value or array.
// 2: Optional.  String representing type of message you want returned.
// add, edit, search, select, message.
// In this module, this arg will typically be 'template'.
// 3: Optional.  Text you want included in the message, like names,
// counts or other static values.  Pass single value or array.
// 4: Optional.  Text is returned exactly as entered (after any applicable translation).
const F = require('./formatter'),
    dateTime = require('shared/helpers/dateTime'),
    date = require('shared/helpers/date');

module.exports = {
    // Not translated.
    resendAlertsDateHelp: (limit) => `
        <ul>
            <li>Both Start and End are required.</li>
            <li>Start must be less than End.</li>
            <li>The span of time between Start and End cannot exceed ${limit} days.</li>
        </ul>
    `,
    docApprovalApproved: (stepName, userFullName, approveDate) => `${stepName} approved by ${userFullName} on ${dateTime(approveDate)}`,
    readAllowColumnHelp: (entryTypes) => {
        let msg = '';
        if (!entryTypes || entryTypes.length === 0) {
            msg = `This role has no additional Read permissions.`;
        }
        else {
            msg = `This role also has Read-only access to all entries of the types selected here.  This overrides all other Read restrictions.`;
        }
        return msg;
    },
    writeAllowDenyColumnHelp: (entryTypes, sitePermissions, restriction) => {
        let msg = '';
        // First check sitePermissions, to see if we need to do anything at all.
        if (sitePermissions) {
            // Check once at the top if they have SitePermission.EntryReadOther.
            let canReadOther = _.some(sitePermissions, perm => perm.toLowerCase() === "entryreadother");
            // Check once at the top if any entry types are selected.
            let noEntryTypesSelected = !entryTypes || entryTypes.length < 1;
            if (!restriction) {     // Allow/Deny not set.
                if (canReadOther) {
                    msg = 'This role has Read and Write access to all entries of any type.';
                }
                else {
                    msg = 'This role has Read and Write access to all entries of any type, but only if they upload them.';
                }
            }
            else if (restriction) {
                if (restriction.toLowerCase() === 'allow') {
                    if (noEntryTypesSelected) {    // Allow set, nothing selected == ALLOW NOTHING
                        msg = 'This role has neither Read nor Write access to entries of any type.';
                    }
                    else {
                        if (canReadOther) {     // Allow set, some entry types selected, EntryReadOther.
                            msg = 'This role has Read and Write access only to entries of the types selected here.';
                        }
                        else {      // Allow set, some entry types selected, no EntryReadOther.
                            msg = 'This role has Read and Write access only to entries of the types selected here, and only if they upload them.';
                        }
                    }
                }
                else if (restriction.toLowerCase() === 'deny') {
                    if (noEntryTypesSelected) {    // Deny set, nothing selected == DENY NOTHING == ALLOW ALL...
                        if (canReadOther) {     // Deny set, nothing selected, EntryReadOther
                            msg = 'This role has Read and Write access to all entries of any type.';
                        }
                        else {      // Deny set, nothing selected, no EntryReadOther
                            msg = 'This role has Read and Write access to all entries of any type, but only if they upload them.';
                        }
                    }
                    else {      // Deny set, some entry types selected, EntryReadOther
                        if (canReadOther) {
                            msg = 'This role has Read and Write access to entries of any type, except the ones selected here.';
                        }
                        else {      // Deny set, some entry types selected, no EntryReadOther
                            msg = 'This role has Read and Write access to entries of any type, except the ones selected here, and only if they upload them.';
                        }
                    }
                }
            }
        }
        return msg;
    },
    invoiceWithSameTypeAlreadyExists: (invoice) => `A <a href="#wellbore/${invoice.wellboreId}/invoice/${invoice.id}/edit">${invoice.invoiceType.name}</a> invoice already exists for this well.`,
    duplicatedAccessGroupUserSelected: (userNames) => `Duplicated user selected, name(s): ${userNames.join(', ')}.`,
    resendAlertsDateRangeOverLimit: (limit) => `The date range is over the limit. Please set date within ${limit} days.`,
    duplicatedUserSelected: (name) => `Duplicated user selected: ${name}`,
    padDelete: (padName) => `Are you sure you want to delete "${padName}"?`,
    changeWellboreCompany: () => `This change may take a while to complete. Once it's done, you should check to make sure all changes are correct. \r\nContinue?`,
    userAddEmailAlreadyExists: (email) => `An account with the email "${email}" already exists. Continue?`,
    requestAccessEmailAlreadyExists: (email) => `An account with the email "${email}" already exists. Click Ok to continue requesting a new account, or click Cancel and contact WellDrive Operations for assistance with your existing account.`,
    deleteUserRoleGroupWithUsers: (userCount) => `This group contains ${userCount} users. Are you sure you want to delete the group?`,
    // -------------------------------------------------------------------------

    // Translated.
    reportDocApprovalWellPropertiesHelp: () => F('report-doc-approval-well-properties-help', 'template', F(['well-properties'])),
    reportDocApprovalWellLimitHelp: () => F('report-doc-approval-well-limit-help', 'template', F(['well'])),
    reportDocApprovalStatusHelp: () => F('report-doc-approval-status-help', 'template', F(['approval-status', 'pending'])),
    reportDocApprovalTemplateHelp: () => F('report-doc-approval-template-help', 'template', F(['template', 'template-criteria'])),
    resendTextLimitHelp: (words) => F('resend-text-limit-help', 'template', words),
    entryTypeAccessPermissionsHelp: (words) => F('entry-type-access-permissions-help', 'template', words),
    entryTypeAccessPermissionsTitle: (words) => F('entry-type-access-permissions-title', 'template', words),
    entryTypeAccessCompaniesTitle: (words) => F('entry-type-access-companies-title', 'template', words),
    entryTypeAccessCompanyHelp: (words) => F('entry-type-access-company-help', 'template', words),
    entryTypeAccessTitle: (words) => F('entry-type-access-title', 'template', words),
    recycleBinTitle: (words) => F('recycle-bin-title', 'template', words),
    myAccountMobileHelp: () => F('my-account-mobile-help', 'template', F(['mobile', 'send-test-message'])),
    myAccountEmailHelp: () => F('my-account-email-help', 'template', F(['email-address', 'send-test-email'])),
    reportCompanyHelp: () => F('report-company-help', 'template', F('report-company')),
    wellPropertiesFor: (name) => F('well-properties-for', 'template', name),
    sortingFor: (name) => F('sorting-for', 'template', name),
    reportFileTypeHelp: () => F('report-file-type-help', 'template', F(['file-type', 'download'])),
    reportScheduledOffHelp: () => F('report-scheduled-off-help', 'template', F(['preview', 'download'])),
    reportWellPropertiesHelp: () => F('report-well-properties-help', 'template', F(['columns', 'well', 'remarks'])),
    reportSortingHelp: () => F('report-sorting-help', 'template', F('sorting')),
    newestVersionOnlyHelp: () => F('newest-version-only-help', 'template', F('newest-version-only')),
    reportTabAssetHelp: () => F('report-tab-asset-help', 'template', F('asset')),
    reportTabWellboreGroupHelp: () => F('report-tab-well-group-help', 'template', F('well-group')),
    reportTabCompanyHelp: () => F('report-tab-company-help', 'template', F(['company', 'entry-type'])),
    reportTypeHelp: () => F('report-type-help', 'template', F(['report-type', 'contents'])),
    cellCarrierMustBeSelectedIfCellNumberNotEmpty: () => F('cell-carrier-required-if-cell-number', 'template', F(['cell-carrier', 'cell-phone'])),
    cellNumberRequiredIfCellCarrierSelected: () => F('cell-number-required-if-cell-carrier', 'template', F(['cell-phone', 'cell-carrier'])),
    filenameTooLong: (words) => F('entry-filename-length', 'template', words),
    filenameLengthWarning: (words) => F('entry-filename-length-warning', 'template', words),
    filenameInvalid: (filename) => F('entry-filename-invalid', 'template', filename),
    filenameIllegalCharacters: (filename) => F('entry-filename-bad-chars', 'template', filename),
    filenameExtraWhitespace: (filename) => F('entry-filename-whitespace', 'template', filename),
    filenameExists: (filename) => F('entry-filename-exists', 'template', filename),
    filenameExistsInUploader: (filename) => F('entry-filename-exists-in-uploader', 'template', filename),
    wellsResetFiltersHelp: () => F('wells-reset-filters-help', 'template', F('reset-filters')),
    wellboreGroupDeleteConfirm: (groupName) => F('wellbore-group-delete-confirm', 'template', groupName),
    companyHeaderTitle: (companyName) => F('company-header-title', 'template', companyName),
    reportFilenameHelp: () => F('report-filename-help', 'template', F('report-file-name')),
    reportTitleHelp: () => F('report-title-help', 'template', F('report-title')),
    reportNameHelp: () => F('report-name-help', 'template', F(['report-name', 'reports-i-created'])),
    stepApprovalInfo: (words) => F('step-approval-info', 'template', words),
    entryEditContact: (words) => F('entry-edit-contact', 'template', words),
    entryRejectionInfo: (words) => F('entry-rejection-info', 'template', words),
    // This is the page/total items count in the upper right corner of the grid.
    // It's special, because it acts like a template, since values are swapped in
    // for placeholders. But Kendo is the one doing that work. We're just returning
    // the proper templated string and Kendo takes it from there.
    gridItemCount: () => F('grid-item-count', null, null, true),
    smsLimit: (words) => F('sms-limit', 'template', words),
    editNotifications: (words) => F('edit-notifications', 'template', words),
    editDocApprovalNotifications: (words) => F('edit-doc-approval-notifications', 'template', words),
    sessionTimeoutExplain: (words) => F('session-timeout-explain', 'template', words),
    smsAlertHelp: (userId) => F('sms-alert-help', 'template', userId),
    searchFilterHelp: () => F('search-filter-help', 'template', F(['search', 'reset-filters', 'save'])),
    searchMatchingFilesYesHelp: () => F('search-only-matching-files-yes-help', 'template', F('filename')),
    searchBuildingResult: (count) => F('search-building-result', 'template', count),
    searchExportLimit: (limit) => F('search-export-limit', 'template', limit),
    searchExportLimitExceeded: (limit) => F('search-export-limit-exceeded', 'template', limit),
    deleteFail: (noun) => F('delete-fail', 'template', F(noun)),
    invalidFilename: (filename) => F('invalid-filename', 'template', filename),
    lessThanNumberOfCharacters: (words) => F('less-than-number-of-characters', 'template', F(words)),
    corruptPdf: (fileName) => F('corrupt-pdf', 'template', fileName),
    downloadTooBig: (downloadSizeLimit) => F('download-too-big', 'template', downloadSizeLimit),
    // JB
    // TODO
    // Refactor this to a modal. The message can be way too big to fit/be easily readable.
    confirmDeleteEntries: (allowed, disallowed) => {
        let toBeDeleted = null;
        let notToBeDeleted = null;
        if (allowed && allowed.length > 0) {
            toBeDeleted = allowed.map((entry) => F('confirm-entry-delete-detail', 'template', F(['Date', dateTime(entry.editDate), 'Entry Type', entry.entryTypeName, 'User', entry.userName], null, null, true)));
        }
        if (disallowed && disallowed.length > 0) {
            notToBeDeleted = disallowed.map((entry) => F('confirm-entry-delete-detail', 'template', F(['Date', dateTime(entry.editDate), 'Entry Type', entry.entryTypeName, 'User', entry.userName], null, null, true)));
        }
        let msg = '';
        if (toBeDeleted) {
            msg = `${F('confirm-entry-delete', null, null, true)} \n ${toBeDeleted}`;
        }
        if (notToBeDeleted) {
            msg = `${msg} \n\n ${F('confirm-entry-not-delete', null, null, true)} \n ${notToBeDeleted}`;
        }
        return msg;
    },
    deleteDone: (noun) => F('delete-done', 'template', F(noun)),
    addAlerts: (alertSpecificity) => F('add-alerts', 'template', alertSpecificity),
    batchEditAlerts: () => F('batch-edit-alerts',  'template'),
    batchDeleteAlerts: () => F('batch-delete-alets', 'template'),
    deleteAlerts: (alertSpecificity) => F('delete-alerts', 'template', alertSpecificity),
    confirmDeleteSavedSearch: (savedSearchName) => F('confirm-delete-saved-search', 'template', savedSearchName),
    allReportsCompanyFilter: () => F('all-reports-company-filter', 'template', F('company')),
    alertEditConfirm: (alertLimitPlural, alertLimitSingle) => F('alert-edit-confirm', 'template', [alertLimitPlural, alertLimitSingle]),
    outputFormatHelp: (userId) => F('output-format-help', 'template', [userId]),
    monthRangeHelp: (startDate, endDate) => F('month-range-help', 'template', [startDate, endDate]),
    wellDataMapHelp: (mapFrom) => F('well-data-map-help', 'template', [mapFrom]),
    prodDataExportTimeZoneHelp: (userId) => F('prod-data-export-time-zone-help', 'template', [userId]),
    wellHasNoPartners: () => F('well-has-no-users-of-role-type', 'template', F('partners', null, null, true)),
    wellHasNoAdministrators: () => F('well-has-no-users-of-role-type', 'template', F('administrators', null, null, true)),
    openedForEditBy: (userName, editDate) => F('opened-for-edit-by', 'template', [userName, date(editDate)]),
    prodDataAccessReviewDetail: (userFullName) => F('prodDataAccessReviewDetail', 'template', [userFullName]),
    columnNameMatchesBuiltInProperty: (columnName) => F('column-name-matches-built-in-property', 'template', [columnName]),
    alertDisabled: (entryTypeName) => F('alert-paused', 'template', [entryTypeName]),
    alertEnabled: (entryTypeName) => F('alert-resumed', 'template', [entryTypeName]),
    maintenanceInvoiceEditHelp: (startDays) => `
        <div class="">
            The amounts for each well are $1/month, accrued according to one of the following scenarios: 
            <ul>
                <dl>
                    <dt>
                        Historical Well
                    </dt>
                    <dd>
                        <ul>
                            <li>
                                The well has never been invoiced for Maintenance before, and <b>${startDays}</b> days have passed since the last upload to that well.  The number is how many months have passed since <b>${startDays}</b> days after the last upload. 
                            </li>
                            <li>
                                The well has been invoiced for Maintenance before.  The number is how many months have passed since a Maintenance invoice containing this well was added. 
                            </li>
                            <li>
                                Maintenance invoicing was turned back on after being turned off. The number is how many months have passed since <b>${startDays}</b> days after maintenance invoicing was enabled. 
                            </li>
                        </ul>
                    </dd>
                    <dt>
                        Non historical Well
                    </dt>
                    <dd>
                        <ul>
                            <li>
                                The well stage was changed from nothing to Flowback, Production or Workover, and <b>${startDays}</b> days have passed since the well stage was changed.  The number is how many months have passed since <b>${startDays}</b> days after the well stage change. 
                            </li>
                            <li>
                                The well stage was changed from Drilling or Completion  to Flowback, Production or Workover, and <b>${startDays}</b> days have passed since the well stage was changed.  The number is how many months have passed since <b>${startDays}</b> days after the well stage change. 
                            </li>
                            <li>
                                The well has been invoiced for Maintenance before, and the well stage has not changed (or was changed to from and to a stage that does not affect maintenance invoicing).  The number is how many months have passed since a Maintenance invoice containing this well was added. 
                            </li>
                            <li>
                                Maintenance invoicing was turned back on after being turned off, and this change occurred more recently than any meaningful changes to the well stage.  The number is how many months have passed since <b>${startDays}</b> days after maintenance invoicing was enabled. 
                            </li>
                        </ul>
                    </dd>
                </dl>
            </ul>
        </div>
    `,
    workoverInvoiceSetupFeeHelp: () => `
    <span>A note to include the amount in <b>Setup Fee</b> will be included in remarks whenever a well meets the following criteria when being invoiced for Workover (should only ever occur once):</span>      
    <ul>
        <li>No existing Workover invoices.</li>      
        <li>No existing Drilling or Completion invoices.</li> 
    `,
    prodInvoiceEditHelp: () => `
    <div>Each invoice calculates how much the operating company gets billed for each partner that had access to production data within a single month.  </div>
    <div>The <b>Billable Wells</b> for each partner company are wells where the following was true: </div>
    <ul>
        <li>Production entries were added that month.  </li>
        <li>The partner company had access to the well that month.  </li>
    </ul>
    <div>Partial months are rounded up.</div>  
    `
};
